import * as yup from "yup";
import Decimal from "decimal.js-light";
import { DateTime } from "luxon";
import { getAccumulatedCargoPieces } from "./get-accumulated-cargo-pieces";

const fieldRequiredErrorTranslateKey = "fieldRequired";

export const timeRangeMin5MinutesErr = "timeRangeMin5MinutesErr";

const toValidDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === "") return null;
  const date = new Date(value as string);
  return isNaN(date.getTime()) ? null : date;
};

const getSchema = (
  stopTimeMandatory = false,
  clientRequiredOnCargos = false,
  hideLoadmeters = false
) => {
  let stopTime = yup
    .string()
    .transform((_, value) => (value === " - " ? "" : value))
    .test("invalid", (value) => {
      if (!value) {
        return true;
      }
      const [timeStart, timeEnd] = value.split(" - ");
      if (timeEnd && !timeStart) {
        return false;
      }
      if (timeEnd && timeStart) {
        if (timeEnd < timeStart) {
          return false;
        }
      }
      return true;
    })
    .test(timeRangeMin5MinutesErr, (value) => {
      if (!value) {
        return true;
      }
      const [timeStart, timeEnd] = value.split(" - ");
      if (timeStart && timeEnd) {
        const [startHour, startMinute] = timeStart.split(":");
        const [endHour, endMinute] = timeEnd.split(":");
        const startMins = Number(startHour) * 60 + Number(startMinute);
        const endMins = Number(endHour) * 60 + Number(endMinute);
        return endMins - startMins >= 5;
      }
      return true;
    });
  if (stopTimeMandatory) {
    stopTime = stopTime.required(fieldRequiredErrorTranslateKey);
  }

  const stopSchema = yup.object({
    loadmeters: yup.string().required(fieldRequiredErrorTranslateKey),
    weight: yup.string(),
    colli: yup.string(),
    length: yup.string(),
    width: yup.string(),
    height: yup.string(),
    cubicMeters: yup.string(),
    ref: yup.string(),
    // tag: yup.string(),
    // The form holds a luxon DateTime here, which yup casts to its string
    // form - an invalid DateTime (a partially edited picker) casts to
    // "Invalid DateTime", which is non-empty and would pass required().
    date: yup
      .string()
      .required()
      .test("valid-date", "invalidDate", (value) => {
        if (value === null || value === undefined) return true; // let .required() report absence
        return toValidDate(value) !== null;
      }),
    openingDate: yup
      .mixed<DateTime>()
      .nullable()
      .test("valid-date", "invalidDate", (value) => {
        if (value === null || value === undefined) return true;
        return value instanceof DateTime && value.isValid;
      }),
    hideForDrivers: yup.boolean(),
    time: stopTime,
    place: yup
      .object({
        coord: yup.object({
          lat: yup
            .number()
            .required()
            .test("valid-latitude", "Invalid latitude", (value) => {
              if (value == null) return true; // let .required() report absence
              return value >= -90 && value <= 90;
            }),
          lon: yup
            .number()
            .required()
            .test("valid-longitude", "Invalid longitude", (value) => {
              if (value == null) return true; // let .required() report absence
              return value >= -180 && value <= 180;
            }),
        }),
      })
      .required(fieldRequiredErrorTranslateKey),
    pieces: yup
      .array()
      .of(
        yup.object({
          unit: yup.mixed().required(),
          quantity: yup.number().required().positive().integer(),
        })
      )
      .nullable(),
  });

  const tempFieldRequiredBasedOnCargoType = {
    is: (v: string) => ["COOL", "FREEZE", "PHARMA"].includes(v),
    then: (s: any) => s.required(fieldRequiredErrorTranslateKey),
    otherwise: (s: any) => s.nullable(),
  };

  const schema = yup
    .object()
    .shape({
      type: yup.string().nullable(),
      qualities: yup.array(),
      requirements: yup.array(),
      products: yup.array(),
      description: yup.string(),
      minTemperature: yup
        .string()
        .when("type", tempFieldRequiredBasedOnCargoType),
      maxTemperature: yup
        .string()
        .when("type", tempFieldRequiredBasedOnCargoType),
      containerNo: yup.string().nullable(),
      orderConfirmationCheck: yup.boolean(),
      note: yup.string(),
      clientId: yup.string().nullable(),
      assignedTeamId: yup.string(),

      // client contact related
      customerContactId: yup.string().nullable(),
      clientContact: yup.string(),
      clientContactPhone: yup.string(),
      clientContactEmail: yup.string(),

      invoiceRef: yup.string().nullable(),
      ref: yup.string().nullable(),
      pickupList: yup
        .array()
        .of(stopSchema)
        .required(fieldRequiredErrorTranslateKey),
      dropoffList: yup
        .array()
        .of(stopSchema)
        .required(fieldRequiredErrorTranslateKey),
      stopUnits: yup
        .array()
        .of(
          yup.object({
            id: yup.string(),
            pickupId: yup.string().required(),
            dropoffId: yup.string().nullable(),
            width: yup.number().nullable(),
            length: yup.number().nullable(),
            height: yup.number().nullable(),
            volume: yup.number().nullable(),
            quantity: yup.number().required().positive().integer(),
            stopUnitTemplateId: yup.string().required(),
            description: yup.string().nullable(),
          })
        )
        .nullable(),
    })
    .test("lmMatch", "lmMatch", (value) => {
      if (hideLoadmeters) {
        return true;
      }
      const { pickupList, dropoffList } = value;
      if (!dropoffList.length) {
        return true;
      }
      const pickupLm = pickupList.reduce(
        (acc, curr) => acc.add(new Decimal(curr.loadmeters || 0)),
        new Decimal(0)
      );
      const dropoffLm = dropoffList.reduce(
        (acc, curr) => acc.add(new Decimal(curr.loadmeters || 0)),
        new Decimal(0)
      );
      // Exact, like the server's check. Loadmeters computed from stop units
      // are rounded so that each side keeps its total
      // (roundLoadmetersPreservingTotal), so they don't drift apart.
      return pickupLm.eq(dropoffLm);
    })
    .test("colliMatch", "colliMatch", (value) => {
      const { pickupList, dropoffList } = value;
      if (!dropoffList.length) {
        return true;
      }
      const pickupColli = pickupList.reduce(
        (acc, curr) => acc.add(new Decimal(curr.colli || 0)),
        new Decimal(0)
      );
      const dropoffColli = dropoffList.reduce(
        (acc, curr) => acc.add(new Decimal(curr.colli || 0)),
        new Decimal(0)
      );
      return pickupColli.eq(dropoffColli);
    })
    .test("unitsMatch", "unitsMatch", (value) => {
      const { pickupList, dropoffList } = value;
      if (!dropoffList.length) {
        return true;
      }
      const pickupUnits = getAccumulatedCargoPieces(pickupList as any);
      const dropoffUnits = getAccumulatedCargoPieces(dropoffList as any);
      return JSON.stringify(pickupUnits) === JSON.stringify(dropoffUnits);
    })
    .test(
      "pickupTimesBeforeDropoffTimes",
      "pickupTimesBeforeDropoffTimes",
      (value) => {
        const { pickupList, dropoffList } = value;
        if (!dropoffList.length) {
          return true;
        }
        // A missing or invalid stop date is reported on the stop's own date
        // field; comparing it here would throw on toISOString() instead.
        const pickupDates = pickupList.map((pickup) => toValidDate(pickup.date));
        const dropoffDates = dropoffList.map((dropoff) =>
          toValidDate(dropoff.date)
        );
        if ([...pickupDates, ...dropoffDates].some((d) => d === null)) {
          return true;
        }
        const pickupsMaxDate = (pickupDates as Date[]).reduce((acc, curr) =>
          curr > acc ? curr : acc
        );

        for (const [i, dropoff] of dropoffList.entries()) {
          const dropoffDate = dropoffDates[i] as Date;
          if (dropoffDate < pickupsMaxDate) {
            return false;
          }
          if (dropoffDate.toISOString() === pickupsMaxDate.toISOString()) {
            const pickupsToCompareTo = pickupList.filter(
              (pickup) =>
                new Date(pickup.date).toISOString() ===
                dropoffDate.toISOString()
            );

            const dropoffTime = dropoff.time;
            for (const pickup of pickupsToCompareTo) {
              const pickupTime = pickup.time;
              if (pickupTime && dropoffTime && pickupTime > dropoffTime) {
                return false;
              }
            }
          }
        }
        return true;
      }
    )
    .test("stopUnitsAssigned", "stopUnitsAssigned", (value) => {
      const { dropoffList, stopUnits } = value;
      // If there are no dropoffs, stopUnits can be unassigned
      if (!dropoffList || dropoffList.length === 0) {
        return true;
      }
      // If there are dropoffs, all stopUnits must have a dropoffId
      if (stopUnits && stopUnits.length > 0) {
        return stopUnits.every(
          (unit: any) => unit.dropoffId !== null && unit.dropoffId !== ""
        );
      }
      return true;
    })
    .test("clientRequired", "clientRequired", (value) => {
      // Only enforced when the org setting is enabled; the schema is rebuilt
      // on every render so this stays in sync with live setting changes.
      if (!clientRequiredOnCargos) {
        return true;
      }
      return !!value.clientId;
    });

  return schema;
};

export default getSchema;
